/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { INotesConfig, ITasksConfig, IDocumentsConfig, IAssessmentsConfig, IContactLogConfig } from 'app/core/models/essette-interfaces';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { IPrintable } from 'app/shared/lib/printing';
import { guid } from 'app/shared/lib/guid';
import { apiCall } from 'app/shared/lib/essette-api';
import { CaseService } from 'app/cm/services/case-service';
import { MemberType } from 'app/cd/services/member-service';
import * as CM from 'app/cm/models/essette-api';

export interface ICaseSummaryModelData {
    caseId: string;
    memberId?: string;
    readonly?: boolean;
}

@autoinject()
export class CaseSummary extends ComponentBase implements IPrintable {

    ////****************** Fields and Properties ************************************////
    protected caseId: string | null = null;
    protected case: CM.Case | null = null;

    protected memberId: string | null = null;
    protected memberType: MemberType = MemberType.Member;

    protected readonly: boolean = false;
    protected canAddNotes: boolean = false;
    protected canViewCareTeam: boolean = false;
    protected hasCaseRoute = false;
    protected isCMOfflineEnabled: boolean = false;

    protected showAssessments: boolean = true;
    protected showCaseCheckOutHistory: boolean = true;
    protected showAssignmentHistory: boolean = true;

    protected id: guid = guid();

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM',
        addTargetProperty: this.addCaseProperty.bind(this),
        flagCodes: ['CM.CaseFile'],
        includeRollup: true
    };

    protected assessmentsConfig: IAssessmentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM'
    };

    protected tasksConfig: ITasksConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        taskDetailRouteName: 'cm.case.task',
        backRouteName: 'cm.case.tasks',
        primaryTargetType: 'CM.CaseTask',
        labelKey: 'core:task.$',
        allowDuplicates: false
    };

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        hasTasks: true,
        addTargetProperty: (document: CM.CaseDocument) => { document.case = { id: this.caseId } }
    };

    protected contactLogConfig: IContactLogConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case'
    };


    ////****************** Element References ***************************************////
    protected $carePlan!: IPrintable;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private caseService: CaseService,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(model: ICaseSummaryModelData): Promise<void> {
        if(this.env.application.code === 'Portal.Providers') {
            this.memberType = MemberType.Patient;
        }

        await this.checkPermissions();
        if(this.env.application.code === 'Portal.Providers') {
            this.documentsConfig.categoryFlagCodes = ['PP.View'];
            model.readonly = true;
            this.showAssessments = false;
            this.showCaseCheckOutHistory = false;
            this.showAssignmentHistory = false;
        }

        this.readonly = model.readonly ?? this.readonly;
        this.caseId = model.caseId;

        // Case loaded for other components to reduce api calls for case/memberId.
        if(!model.memberId) {
            this.case = await this.loadCase(this.caseId);
            this.memberId = this.case.member?.id || null;
            this.tasksConfig.memberId = this.case.member?.id || undefined;
        } else {
            this.memberId = model.memberId;
            this.tasksConfig.memberId = model.memberId;
        }
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canAddNotes = this.env.application.code === 'Portal.Providers' || this.accountContext.hasPermission('CM.CaseNote.Insert.SummaryInsert');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.canViewCareTeam = this.accountContext.hasRouteAccess('cm.case.careteam');
        this.isCMOfflineEnabled = this.env.config.integrations.includes('cm-offline');
    }

    public async prepareForPrinting(): Promise<void> {
        // prepare care plan for printing
        if(this.$carePlan && this.$carePlan.prepareForPrinting) {
            this.$carePlan.prepareForPrinting();
        }
    }

    private addCaseProperty(note: CM.CaseNote): void {
        note.case = { id: this.caseId };
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCase(caseId: string): Promise<CM.Case> {

        const includes: string[] = [];
        const fields: string[] = ['member.id'];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadCase(caseId, includes, fields);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}

/**
 * Opens the case summary modal with the global dialog
 * @param $case The case information to use
 */
export function openCaseSummary($case: CM.Case, hasCaseRoute: boolean = false, readonly: boolean = false): void {
    if(!$case || !$case.id) {
        throw new Error('Case is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: 'cm.case.summary',
        title: {
            key: 'cm:routes.case.summary.$title',
            replace: { number: $case.number }
        },
        print: true,
        popout: {
            route: 'cm.case.summary',
            params: { caseId: $case.id }
        }
    };

    const modelData: ICaseSummaryModelData = {
        caseId: $case.id,
        readonly: readonly
    };

    if($case.member?.id) modelData.memberId = $case.member.id;

    const modalData: IModalData = {
        componentId: 'app/cm/components/summary/case-summary',
        modelData: modelData,
        dialogOptions: hasCaseRoute ? {
            ...dialogOptions,
            openRecord: {
                route: 'cm.case',
                params: {
                    caseId: $case.id,
                },
                tooltipDesc: {
                    key: 'ui:actions.goTo',
                    replace: {
                        what: '$t(cm:case.$)',
                        item: $case.number
                    }
                }
            }
        }
            :
            dialogOptions
    };

    openGlobalModal(modalData);
}
